/**
 * OKLCH / sRGB / WCAG helpers for guided custom color-sets.
 * Same contrast thresholds as scripts/check-theme-contrast.mjs (spec §3.4).
 */

export const WCAG_AA = 4.5;
export const WCAG_AA_LARGE = 3;

export type OklchTuple = readonly [L: number, C: number, H: number];

const OKLCH_RE =
  /^oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*[\d.%]+)?\s*\)$/i;
const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function parseOklch(value: string): OklchTuple | null {
  const m = String(value).trim().match(OKLCH_RE);
  if (!m) return null;
  const L = Number(m[1]);
  const C = Number(m[2]);
  const H = Number(m[3]);
  if (![L, C, H].every((n) => Number.isFinite(n))) return null;
  return [L, C, H];
}

export function formatOklch(L: number, C: number, H: number): string {
  const l = clamp(L, 0, 1);
  const c = Math.max(0, C);
  const h = ((H % 360) + 360) % 360;
  return `oklch(${round3(l)} ${round3(c)} ${round3(h)})`;
}

export function isOklchColorString(value: string): boolean {
  return parseOklch(value) !== null;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function expandHex(hex: string): string {
  if (hex.length === 3) {
    return hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return hex;
}

export function parseHexToSrgb(value: string): [number, number, number] | null {
  const m = String(value).trim().match(HEX_RE);
  const raw = m?.[1];
  if (!raw) return null;
  const hex = expandHex(raw.toLowerCase());
  const r = Number.parseInt(hex.slice(0, 2), 16) / 255;
  const g = Number.parseInt(hex.slice(2, 4), 16) / 255;
  const b = Number.parseInt(hex.slice(4, 6), 16) / 255;
  return [r, g, b];
}

/** Linear sRGB channel → 0..1. */
function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function linearToSrgb(c: number): number {
  const x = clamp(c, 0, 1);
  return x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
}

export function oklchToSrgb(L: number, C: number, H: number): [number, number, number] {
  const h = (H * Math.PI) / 180;
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bl = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
  return [linearToSrgb(r), linearToSrgb(g), linearToSrgb(bl)];
}

export function srgbToOklch(r: number, g: number, b: number): OklchTuple {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bCh = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const C = Math.sqrt(a * a + bCh * bCh);
  let H = (Math.atan2(bCh, a) * 180) / Math.PI;
  if (H < 0) H += 360;
  return [clamp(L, 0, 1), Math.max(0, C), H];
}

export function hexToOklchString(hex: string): string | null {
  const rgb = parseHexToSrgb(hex);
  if (!rgb) return null;
  const [L, C, H] = srgbToOklch(...rgb);
  return formatOklch(L, C, H);
}

export function oklchToHex(value: string): string | null {
  const parsed = parseOklch(value);
  if (!parsed) return null;
  const [r, g, b] = oklchToSrgb(...parsed);
  const toByte = (c: number) =>
    clamp(Math.round(clamp(c, 0, 1) * 255), 0, 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toByte(r)}${toByte(g)}${toByte(b)}`;
}

/** HSV for guided UI pickers (H 0–360, S/V 0–1). Stored colors stay OKLCH. */
export type HsvTuple = readonly [h: number, s: number, v: number];

export function srgbToHsv(r: number, g: number, b: number): HsvTuple {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d > 1e-8) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max <= 1e-8 ? 0 : d / max;
  return [h, s, max];
}

export function hsvToSrgb(h: number, s: number, v: number): [number, number, number] {
  const hh = ((h % 360) + 360) % 360;
  const c = v * s;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = v - c;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (hh < 60) [rp, gp, bp] = [c, x, 0];
  else if (hh < 120) [rp, gp, bp] = [x, c, 0];
  else if (hh < 180) [rp, gp, bp] = [0, c, x];
  else if (hh < 240) [rp, gp, bp] = [0, x, c];
  else if (hh < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return [rp + m, gp + m, bp + m];
}

function srgbToHex(r: number, g: number, b: number): string {
  const toByte = (c: number) =>
    clamp(Math.round(clamp(c, 0, 1) * 255), 0, 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toByte(r)}${toByte(g)}${toByte(b)}`;
}

export function hsvToHex(h: number, s: number, v: number): string {
  return srgbToHex(...hsvToSrgb(h, s, v));
}

export function oklchToHsv(value: string): HsvTuple | null {
  const parsed = parseOklch(value);
  if (!parsed) return null;
  return srgbToHsv(...oklchToSrgb(...parsed));
}

export function hsvToOklchString(h: number, s: number, v: number): string {
  return formatOklch(...srgbToOklch(...hsvToSrgb(h, s, v)));
}

/**
 * Accept hex or OKLCH; always normalize to `oklch(L C H)`.
 */
export function normalizeToOklchString(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (parseOklch(trimmed)) {
    const [L, C, H] = parseOklch(trimmed)!;
    return formatOklch(L, C, H);
  }
  return hexToOklchString(trimmed);
}

export function relativeLuminance(srgb: [number, number, number]): number {
  const [r, g, b] = srgb.map(srgbToLinear) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(fg: OklchTuple, bg: OklchTuple): number {
  const a = relativeLuminance(oklchToSrgb(...fg));
  const b = relativeLuminance(oklchToSrgb(...bg));
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  return (hi + 0.05) / (lo + 0.05);
}

export type ContrastPairRole = 'textMain' | 'textMuted' | 'brandPrimary';

export type ContrastPairResult = {
  role: ContrastPairRole;
  ratio: number;
  min: number;
  pass: boolean;
  /** Soft AA body warning for brand (passes AA-large only). */
  warn?: boolean;
};

export type ColorSetContrastReport = {
  pairs: ContrastPairResult[];
  /** Any pair below its minimum. */
  hasFailures: boolean;
  /** Brand passes AA-large but not AA body. */
  hasWarnings: boolean;
};

/**
 * Evaluate the same pairs as the registry contrast gate.
 */
export function evaluateColorSetContrast(tokens: {
  bgBase: string;
  textMain: string;
  textMuted: string;
  brandPrimary: string;
}): ColorSetContrastReport {
  const bg = parseOklch(tokens.bgBase);
  const textMain = parseOklch(tokens.textMain);
  const textMuted = parseOklch(tokens.textMuted);
  const brand = parseOklch(tokens.brandPrimary);

  const pairs: ContrastPairResult[] = [];
  if (bg && textMain) {
    const ratio = contrastRatio(textMain, bg);
    pairs.push({
      role: 'textMain',
      ratio,
      min: WCAG_AA,
      pass: ratio >= WCAG_AA,
    });
  }
  if (bg && textMuted) {
    const ratio = contrastRatio(textMuted, bg);
    pairs.push({
      role: 'textMuted',
      ratio,
      min: WCAG_AA,
      pass: ratio >= WCAG_AA,
    });
  }
  if (bg && brand) {
    const ratio = contrastRatio(brand, bg);
    pairs.push({
      role: 'brandPrimary',
      ratio,
      min: WCAG_AA_LARGE,
      pass: ratio >= WCAG_AA_LARGE,
      warn: ratio >= WCAG_AA_LARGE && ratio < WCAG_AA,
    });
  }

  return {
    pairs,
    hasFailures: pairs.some((p) => !p.pass),
    hasWarnings: pairs.some((p) => p.warn),
  };
}

/**
 * Nudge only Lightness (keep C/H) until contrast vs bg meets `minRatio`.
 * Returns null if unreachable within L ∈ [0.05, 0.98].
 */
export function suggestOklchForContrast(
  fgCss: string,
  bgCss: string,
  minRatio: number,
): string | null {
  const fg = parseOklch(fgCss);
  const bg = parseOklch(bgCss);
  if (!fg || !bg) return null;
  if (contrastRatio(fg, bg) >= minRatio) {
    return formatOklch(...fg);
  }

  const [, C, H] = fg;
  const bgLum = relativeLuminance(oklchToSrgb(...bg));
  const preferLight = bgLum < 0.5;
  const start = preferLight ? 0.98 : 0.05;
  const end = preferLight ? 0.05 : 0.98;
  const step = preferLight ? -0.01 : 0.01;

  for (let L = start; preferLight ? L >= end : L <= end; L += step) {
    const candidate: OklchTuple = [L, C, H];
    if (contrastRatio(candidate, bg) >= minRatio) {
      return formatOklch(L, C, H);
    }
  }
  return null;
}
